import React, { useState } from 'react';
import { PostCard } from './PostCard';
import { PersonCard } from './PersonCard';
import { linkedInPosts, peopleProfiles } from '../utils/mockData';
import { FilterIcon, UsersIcon, ArrowRightIcon, TrendingUpIcon, AlertCircleIcon, BarChart2Icon, EyeIcon } from 'lucide-react';
import { AnalyticsPanel } from './AnalyticsPanel';
import { PersonModal } from './PersonModal';
import { IntentSignalAnalytics } from './IntentSignalAnalytics';
interface DashboardProps {
  onPersonSelect: (personId: number) => void;
  onNavigateToTab: (tab: string) => void;
  onMarkInteresting: (personId: number) => void;
  onMarkNotRelevant: (personId: number) => void;
}
export const Dashboard: React.FC<DashboardProps> = ({
  onPersonSelect,
  onNavigateToTab,
  onMarkInteresting,
  onMarkNotRelevant
}) => {
  const [selectedPost, setSelectedPost] = useState<number | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<number | null>(null);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [timeRange, setTimeRange] = useState('7days');
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [activeInsightTab, setActiveInsightTab] = useState('all');
  const handleViewEngagement = (postId: number) => {
    setSelectedPost(postId);
    setSelectedProfile(null);
  };
  const handlePersonSelect = (personId: number) => {
    setSelectedProfile(personId);
    setShowPersonModal(true);
  };
  const handleClosePersonModal = () => {
    setShowPersonModal(false);
  };
  const handleNavigateToMessages = (personId: number) => {
    onPersonSelect(personId);
    onNavigateToTab('messages');
  };
  const getEngagedPeople = (postId: number, filterBy?: string) => {
    let filteredPeople = peopleProfiles.filter(person => person.interactions.some(interaction => interaction.postId === postId));
    if (filterBy === 'high') {
      filteredPeople = filteredPeople.filter(p => p.relevance === 'high');
    } else if (filterBy === 'new') {
      // Filter for people with interactions in the last 3 days
      filteredPeople = filteredPeople.filter(p => p.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 3));
    }
    return filteredPeople;
  };
  const getHighIntentCount = (postId: number) => {
    return getEngagedPeople(postId, 'high').length;
  };
  const getNewEngagementCount = (postId: number) => {
    return getEngagedPeople(postId, 'new').length;
  };
  // Get intent signals summary
  const intentSignalsSummary = {
    total: peopleProfiles.length,
    high: peopleProfiles.filter(p => p.relevance === 'high').length,
    medium: peopleProfiles.filter(p => p.relevance === 'medium').length,
    low: peopleProfiles.filter(p => p.relevance === 'low').length,
    new: peopleProfiles.filter(p => p.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 2)).length
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-117">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0" data-id="element-118">
        <div data-id="element-119">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-120">
            LinkedIn Activity Dashboard
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-121">
            Monitor engagement and detect intent signals from your LinkedIn
            network
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-122">
          <select className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={timeRange} onChange={e => setTimeRange(e.target.value)} data-id="element-123">
            <option value="7days" data-id="element-124">Last 7 days</option>
            <option value="30days" data-id="element-125">Last 30 days</option>
            <option value="90days" data-id="element-126">Last 90 days</option>
          </select>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-127">
            <FilterIcon size={16} className="mr-2" data-id="element-128" />
            Filters
          </button>
        </div>
      </div>

      {/* Intent Signals Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-id="element-129">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-130">
          <div className="flex items-center justify-between mb-2" data-id="element-131">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-132">Total Signals</h3>
            <span className="p-1.5 rounded-full bg-blue-100" data-id="element-133">
              <UsersIcon size={16} className="text-blue-600" data-id="element-134" />
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-135">
            {intentSignalsSummary.total}
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-136">
            People showing intent
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-137">
          <div className="flex items-center justify-between mb-2" data-id="element-138">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-139">High Intent</h3>
            <span className="p-1.5 rounded-full bg-green-100" data-id="element-140">
              <TrendingUpIcon size={16} className="text-green-600" data-id="element-141" />
            </span>
          </div>
          <div className="text-2xl font-bold text-green-600" data-id="element-142">
            {intentSignalsSummary.high}
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-143">Ready for outreach</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-144">
          <div className="flex items-center justify-between mb-2" data-id="element-145">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-146">Medium Intent</h3>
            <span className="p-1.5 rounded-full bg-yellow-100" data-id="element-147">
              <EyeIcon size={16} className="text-yellow-600" data-id="element-148" />
            </span>
          </div>
          <div className="text-2xl font-bold text-yellow-600" data-id="element-149">
            {intentSignalsSummary.medium}
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-150">Showing interest</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-151">
          <div className="flex items-center justify-between mb-2" data-id="element-152">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-153">Low Intent</h3>
            <span className="p-1.5 rounded-full bg-gray-100" data-id="element-154">
              <BarChart2Icon size={16} className="text-gray-600" data-id="element-155" />
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-600" data-id="element-156">
            {intentSignalsSummary.low}
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-157">Minimal engagement</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-158">
          <div className="flex items-center justify-between mb-2" data-id="element-159">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-160">New Signals</h3>
            <span className="p-1.5 rounded-full bg-purple-100" data-id="element-161">
              <AlertCircleIcon size={16} className="text-purple-600" data-id="element-162" />
            </span>
          </div>
          <div className="text-2xl font-bold text-purple-600" data-id="element-163">
            {intentSignalsSummary.new}
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-164">Last 48 hours</div>
        </div>
      </div>

      <AnalyticsPanel data-id="element-165" />
      <div className="flex justify-between items-center mb-4" data-id="element-166">
        <button onClick={() => setShowAdvancedAnalytics(!showAdvancedAnalytics)} className="text-sm text-[#f9b30f] hover:text-[#e0a40e] font-medium flex items-center" data-id="element-167">
          {showAdvancedAnalytics ? 'Hide' : 'Show'} Intent Signal Analytics
          <ArrowRightIcon size={16} className={`ml-1 transition-transform ${showAdvancedAnalytics ? 'transform rotate-90' : ''}`} data-id="element-168" />
        </button>
        {showAdvancedAnalytics && <button onClick={() => onNavigateToTab('people')} className="text-sm text-[#f9b30f] hover:text-[#e0a40e] font-medium flex items-center" data-id="element-169">
            View All Prospects
            <ArrowRightIcon size={16} className="ml-1" data-id="element-170" />
          </button>}
      </div>

      {showAdvancedAnalytics && <IntentSignalAnalytics data-id="element-171" />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-172">
        <div className="lg:col-span-1" data-id="element-173">
          <div className="mb-4 flex justify-between items-center" data-id="element-174">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-175">
              Recent LinkedIn Posts
            </h2>
            <span className="text-sm text-gray-500" data-id="element-176">
              {linkedInPosts.length} posts
            </span>
          </div>
          <div className="space-y-4" data-id="element-177">
            {linkedInPosts.map(post => <PostCard key={post.id} post={post} onViewEngagement={handleViewEngagement} isSelected={selectedPost === post.id} highIntentCount={getHighIntentCount(post.id)} newEngagementCount={getNewEngagementCount(post.id)} data-id="element-178" />)}
          </div>
        </div>
        <div className="lg:col-span-2" data-id="element-179">
          {selectedPost ? <>
              <div className="mb-4" data-id="element-180">
                <div className="flex justify-between items-center" data-id="element-181">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-182">
                    People Who Engaged
                    <span className="ml-2 text-sm font-normal text-gray-500" data-id="element-183">
                      (
                      {getEngagedPeople(selectedPost, activeInsightTab === 'all' ? undefined : activeInsightTab).length}{' '}
                      people)
                    </span>
                  </h2>
                  <button onClick={() => onNavigateToTab('people')} className="inline-flex items-center text-sm font-medium text-[#f9b30f] hover:text-[#e0a40e]" data-id="element-184">
                    View All People
                    <ArrowRightIcon size={16} className="ml-1" data-id="element-185" />
                  </button>
                </div>
                <div className="mt-2 border-b border-gray-200" data-id="element-186">
                  <nav className="-mb-px flex space-x-6" data-id="element-187">
                    <button onClick={() => setActiveInsightTab('all')} className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeInsightTab === 'all' ? 'border-[#f9b30f] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-188">
                      All ({getEngagedPeople(selectedPost).length})
                    </button>
                    <button onClick={() => setActiveInsightTab('high')} className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeInsightTab === 'high' ? 'border-[#f9b30f] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-189">
                      High Intent (
                      {getEngagedPeople(selectedPost, 'high').length})
                    </button>
                    <button onClick={() => setActiveInsightTab('new')} className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeInsightTab === 'new' ? 'border-[#f9b30f] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-190">
                      New ({getEngagedPeople(selectedPost, 'new').length})
                    </button>
                  </nav>
                </div>
              </div>
              {getEngagedPeople(selectedPost, activeInsightTab === 'all' ? undefined : activeInsightTab).length > 0 ? <div className="space-y-4" data-id="element-191">
                  {getEngagedPeople(selectedPost, activeInsightTab === 'all' ? undefined : activeInsightTab).map(person => <PersonCard key={person.id} person={person} onSelect={handlePersonSelect} isSelected={selectedProfile === person.id} onMarkInteresting={onMarkInteresting} onMarkNotRelevant={onMarkNotRelevant} data-id="element-192" />)}
                </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-193">
                  <UsersIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-194" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-195">
                    No{' '}
                    {activeInsightTab !== 'all' ? activeInsightTab + ' intent ' : ''}
                    engagement found
                  </h3>
                  <p className="text-gray-500" data-id="element-196">
                    {activeInsightTab !== 'all' ? `Try selecting a different filter or check other posts.` : `This post hasn't received any engagement yet.`}
                  </p>
                </div>}
            </> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center h-64 flex flex-col items-center justify-center" data-id="element-197">
              <UsersIcon size={48} className="text-gray-400 mb-4" data-id="element-198" />
              <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-199">
                Select a post to view engagement
              </h3>
              <p className="text-gray-500" data-id="element-200">
                Click "View Engagement" on any post to see who interacted with
                it and analyze intent signals.
              </p>
            </div>}
        </div>
      </div>

      {showPersonModal && selectedProfile && <PersonModal personId={selectedProfile} onClose={handleClosePersonModal} onNavigateToMessages={handleNavigateToMessages} data-id="element-201" />}
    </div>;
};